import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { CurrencyInput } from 'cpool-components/input/CurrencyInput';
import { NumberInput } from 'cpool-components/input/NumberInput';
import { SegmentedControl } from 'cpool-components/input/SegmentedControl';
import { LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ItemDeleteButton from '../General/ItemDeleteButton.tsx';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useApplicantAttributionOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type ChildProps = {
    childIndex: number;
    childNumber: number;
    removeChild: (index: number) => void;
    hasSecondApplicant: boolean;
    onApplicantAttributionChange: (index: number, value: APPLICANT_ATTRIBUTION_BOTH_TYPE) => void;
};

const Child: React.FC<ChildProps> = ({
    childIndex,
    childNumber,
    hasSecondApplicant,
    onApplicantAttributionChange,
    removeChild,
}) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { t } = useTranslation();
    const { control } = useFormContext<SchemaLivingSituationForm>();
    const attributionOptions = useApplicantAttributionOptions();

    const attribution = useWatch({
        name: `kinder.${childIndex}.gehoertZuAntragsteller`,
    }) as APPLICANT_ATTRIBUTION_BOTH_TYPE;

    const isChangeChildDisabled = !canExecuteApplicantCommand({
        applicant: 1,
        commandName: LOAN_REQUEST_COMMANDS.changeChild,
        permissions,
    });

    return (
        <FormCard>
            <div className="flex items-center justify-between">
                <h6 className="text-sm font-bold text-midnight">
                    {t('LIVING_SITUATION_DATA.CHILDREN.CHILDREN_TITLE', { childNr: childNumber.toString() })}
                </h6>
                <ItemDeleteButton onClick={() => removeChild(childIndex)} disabled={isChangeChildDisabled} />
            </div>
            {hasSecondApplicant && (
                <div className="flex items-start gap-2.5">
                    <Label className="h-8 w-[175px] shrink-0 justify-start">
                        {t('LIVING_SITUATION_DATA.CHILDREN.CHILD_ATTRIBUTION')}
                    </Label>
                    <SegmentedControl<APPLICANT_ATTRIBUTION_BOTH_TYPE>
                        id={`kinder.${childIndex}.gehoertZuAntragsteller`}
                        ariaLabel={t('LIVING_SITUATION_DATA.CHILDREN.CHILD_ATTRIBUTION')}
                        options={attributionOptions}
                        value={attribution ?? null}
                        onChange={(value) => onApplicantAttributionChange(childIndex, value)}
                        disabled={
                            isChangeChildDisabled ||
                            !canExecuteApplicantCommand({
                                applicant: 2,
                                commandName: LOAN_REQUEST_COMMANDS.changeChild,
                                permissions,
                            })
                        }
                    />
                </div>
            )}
            <FormField<SchemaLivingSituationForm, number | null> control={control} name={`kinder.${childIndex}.alter`}>
                {({ error, onChange, value }) => (
                    <NumberInput
                        id={`kinder.${childIndex}.alter`}
                        label={t('LIVING_SITUATION_DATA.CHILDREN.CHILD_AGE')}
                        labelClassName="h-8 w-[175px] shrink-0 justify-start"
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        allowDecimals={false}
                        disabled={isChangeChildDisabled}
                        error={error}
                    />
                )}
            </FormField>
            <FormField<SchemaLivingSituationForm, number | null>
                control={control}
                name={`kinder.${childIndex}.kindergeldHoehe`}
            >
                {({ error, onChange, value }) => (
                    <CurrencyInput
                        id={`kinder.${childIndex}.kindergeldHoehe`}
                        label={t('LIVING_SITUATION_DATA.CHILDREN.CHILD_SUPPORT')}
                        labelClassName="h-8 w-[175px] shrink-0 justify-start"
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        disabled={isChangeChildDisabled}
                        error={error}
                    />
                )}
            </FormField>
            <FormField<SchemaLivingSituationForm, number | null>
                control={control}
                name={`kinder.${childIndex}.unterhaltseinnahmenMonatlich`}
            >
                {({ error, onChange, value }) => (
                    <CurrencyInput
                        id={`kinder.${childIndex}.unterhaltseinnahmenMonatlich`}
                        label={t('LIVING_SITUATION_DATA.CHILDREN.MAINTENANCE_INCOME')}
                        labelClassName="h-8 w-[175px] shrink-0 justify-start"
                        className="w-full"
                        value={value}
                        onChange={onChange}
                        min={0}
                        disabled={isChangeChildDisabled}
                        error={error}
                    />
                )}
            </FormField>
        </FormCard>
    );
};

export default Child;
